import { AnimatePresence, motion, Variants } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLogo from "../ui/AppLogo";

// Type definitions
interface LinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  [key: string]: any;
}

interface NavigationItem {
  name: string;
  href: string;
  paths: string[];
}

const Link: React.FC<LinkProps> = ({ to, children, className, ...props }) => (
  <a href={to} className={className} {...props}>
    {children}
  </a>
);

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [lastScrollY, setLastScrollY] = useState<number>(0);
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [currentPath, setCurrentPath] = useState<string>(
    window.location.pathname
  );

  const navigate = useNavigate();

  useEffect(() => {
    // Update current path when it changes
    const handleLocationChange = (): void => {
      setCurrentPath(window.location.pathname);
    };

    // Listen for navigation changes (for SPA routing)
    window.addEventListener("popstate", handleLocationChange);

    return () => {
      window.removeEventListener("popstate", handleLocationChange);
    };
  }, []);

  useEffect(() => {
    const handleScroll = (): void => {
      const currentScrollY = window.scrollY;

      // Determine if scrolled from top
      setIsScrolled(currentScrollY > 20);

      // Hide/show navbar based on scroll direction
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const toggleMobileMenu = (): void => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = (): void => {
    setIsMobileMenuOpen(false);
  };

  // Navigation items with path matching
  const navigationItems: NavigationItem[] = [
    { name: "Features", href: "/features", paths: ["/features"] },
    { name: "Pricing", href: "/pricing", paths: ["/pricing"] },
    { name: "FAQs", href: "/faqs", paths: ["/faqs"] },
    { name: "Contact us", href: "/contact", paths: ["/contact"] },
  ];

  // Check if current path matches any of the item's paths
  const isActiveLink = (item: NavigationItem): boolean => {
    return item.paths.some(
      (path) =>
        path === currentPath || (path !== "/" && currentPath.startsWith(path))
    );
  };

  const onLinkClick = (href: string): void => {
    navigate(href);
    setCurrentPath(href);
    closeMobileMenu();
  };

  // Animation variants
  const navbarVariants: Variants = {
    visible: {
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
    hidden: {
      y: -100,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
  };

  const mobileMenuVariants: Variants = {
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
    open: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
  };

  const menuItemVariants: Variants = {
    closed: { opacity: 0, y: -10 },
    open: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.3,
      },
    }),
  };

  const buttonVariants: Variants = {
    hover: {
      scale: 1.05,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
    tap: {
      scale: 0.95,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
  };

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 text-hColor ${
        isScrolled
          ? "opacity-[#F1F1F1] bg-transparent backdrop-blur-md shadow-md border-b border-[#F1F1F1]"
          : "bg-transparent"
      }`}
      variants={navbarVariants}
      animate={isVisible ? "visible" : "hidden"}
      initial="visible"
    >
      <div className="max-w-[90vw] mx-auto px-2 xs:px-0 sm:px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12 xs:h-14 sm:h-16 md:h-18 lg:h-20">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex-shrink-0"
          >
            <button
              onClick={() => navigate("/")}
              className={`flex items-center space-x-2 xs:space-x-3 transition-colors duration-300 ${
                isScrolled ? "text-hColor" : "text-hColor"
              }`}
            >
              <AppLogo />
            </button>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <motion.div
              className={`text-hColor flex items-center space-x-4 lg:space-x-6 13in:space-x-8 rounded-[10px] px-3 lg:px-4 13in:px-6 py-2 lg:py-3 transition-all duration-300 ${
                isScrolled ? "backdrop-blur-sm" : "backdrop-blur-sm"
              }`}
              whileHover={{ scale: 1.02 }}
            >
              {navigationItems.map((item, index) => {
                const isActive = isActiveLink(item);
                return (
                  <motion.div
                    key={item.name}
                    whileHover={{ y: -2 }}
                    whileTap={{ y: 0 }}
                    className="relative"
                  >
                    <button
                      // to={item.href}
                      className={`text-sm lg:text-base 13in:text-lg font-medium transition-colors duration-300 relative pb-1 ${
                        isActive
                          ? isScrolled
                            ? "text-hColor"
                            : "text-hColor"
                          : isScrolled
                          ? "text-pColor hover:text-hColor"
                          : "text-pColor hover:text-hColor"
                      }`}
                      onClick={() => onLinkClick(item.href)}
                    >
                      {item.name}
                      {isActive && (
                        <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" />
                      )}
                    </button>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>

          {/* Tablet Navigation (md) */}
          <div className="hidden md:block lg:hidden">
            <motion.div
              className={`text-hColor flex items-center space-x-4 rounded-[8px] px-3 py-2 transition-all duration-300 ${
                isScrolled
                  ? "bg-[#F1F1F1] backdrop-blur-sm"
                  : "bg-white/10 backdrop-blur-sm"
              }`}
              whileHover={{ scale: 1.02 }}
            >
              {navigationItems.map((item, index) => {
                const isActive = isActiveLink(item);
                return (
                  <motion.div
                    key={item.name}
                    whileHover={{ y: -2 }}
                    whileTap={{ y: 0 }}
                    className="relative"
                  >
                    <button
                      // to={item.href}
                      className={`text-sm font-medium transition-colors duration-300 relative pb-1 ${
                        isActive
                          ? isScrolled
                            ? "text-hColor"
                            : "text-hColor"
                          : isScrolled
                          ? "text-pColor hover:text-hColor"
                          : "text-pColor hover:text-hColor"
                      }`}
                      onClick={() => onLinkClick(item.href)}
                    >
                      {item.name}
                      {isActive && (
                        <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" />
                      )}
                    </button>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>

          {/* CTA Button and Mobile Menu Button */}
          <div className="flex items-center space-x-2 xs:space-x-3 sm:space-x-4">
            {/* CTA Button - Responsive sizing */}
            <motion.button
              onClick={() => navigate("/sign-up")}
              type="button"
              className={`hidden sm:block items-center justify-center rounded-lg border border-primary font-medium text-2sm transition-all duration-300 min-w-[120px] sm:min-w-[140px] md:min-w-[160px] lg:min-w-[180px] 13in:min-w-[200px] 15in:min-w-[232px] h-[40px] sm:h-[45px] md:h-[50px] lg:h-[40px] ${
                isScrolled
                  ? "border-primary text-hColor bg-white hover:bg-gray-50 hover:border-gray-400"
                  : "border-primary text-hColor bg-white hover:bg-gray-50 hover:border-gray-400 backdrop-blur-sm"
              }`}
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <span className="sm:hidden">Start</span>
              <span className="hidden sm:inline md:hidden">Get started</span>
              <span className="hidden md:inline">Get started</span>
            </motion.button>

            {/* Mobile Menu Button */}
            <motion.button
              type="button"
              className={`md:hidden items-center justify-center h-[30px] xs:h-[32px] sm:h-[35px] p-1.5 xs:p-2 rounded-lg transition-colors duration-300 ${
                isScrolled
                  ? "text-hColor hover:bg-gray-100"
                  : "text-hColor hover:bg-gray-100"
              }`}
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <motion.div
                animate={isMobileMenuOpen ? "open" : "closed"}
                className="w-5 xs:w-6 h-5 xs:h-6 relative"
              >
                <motion.span
                  className="absolute top-0 left-0 w-full h-0.5 bg-current rounded-full"
                  variants={{
                    closed: { rotate: 0, y: 0 },
                    open: { rotate: 45, y: 8 },
                  }}
                  transition={{ duration: 0.3 }}
                />
                <motion.span
                  className="absolute top-2 left-0 w-full h-0.5 bg-current rounded-full"
                  variants={{
                    closed: { opacity: 1 },
                    open: { opacity: 0 },
                  }}
                  transition={{ duration: 0.3 }}
                />
                <motion.span
                  className="absolute top-4 left-0 w-full h-0.5 bg-current rounded-full"
                  variants={{
                    closed: { rotate: 0, y: 0 },
                    open: { rotate: -45, y: -8 },
                  }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className={`block md:hidden lg:block xl:hidden border-t transition-colors duration-300 ${
                isScrolled
                  ? "border-white/20 bg-black/50 backdrop-blur-md"
                  : "border-white/20 bg-black/50 backdrop-blur-md"
              }`}
              variants={mobileMenuVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              <div className="px-3 xs:px-4 py-4 xs:py-5 sm:py-6 space-y-3 xs:space-y-4">
                {navigationItems.map((item, index) => {
                  const isActive = isActiveLink(item);
                  return (
                    <motion.div
                      key={item.name}
                      variants={menuItemVariants}
                      custom={index}
                      initial="closed"
                      animate="open"
                      exit="closed"
                      className="relative"
                    >
                      <Link
                        to={item.href}
                        className={`block text-base xs:text-lg font-medium transition-colors duration-300 py-2 relative ${
                          isActive
                            ? isScrolled
                              ? "text-gray-900"
                              : "text-white"
                            : isScrolled
                            ? "text-hColor hover:text-gray-900"
                            : "text-white/70 hover:text-white"
                        }`}
                        onClick={() => {
                          setCurrentPath(item.href);
                          closeMobileMenu();
                        }}
                      >
                        {item.name}
                        {isActive && (
                          <motion.div
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{
                              type: "spring",
                              stiffness: 300,
                              damping: 30,
                            }}
                          />
                        )}
                      </Link>
                    </motion.div>
                  );
                })}

                {/* Mobile CTA Button */}
                <motion.button
                  onClick={() => {
                    navigate("/sign-up");
                    closeMobileMenu();
                  }}
                  type="button"
                  className={`sm:hidden w-full border rounded-[8px] px-4 py-2.5 xs:py-3 text-sm xs:text-base font-medium transition-all duration-300 ${
                    isScrolled
                      ? "border-primary text-hColor bg-white hover:bg-gray-50"
                      : "border-primary text-hColor bg-white hover:bg-gray-50 backdrop-blur-sm"
                  }`}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  Get started
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
